import { Login } from "@mcleod/common";
import { ClickEvent, DropdownItem, Event, Label, Layout, Snackbar } from "@mcleod/components";
import { Alignment, Api, AuthType, HorizontalAlignment, LogManager, Navigation, StringUtil } from "@mcleod/core";
import { AutogenLayoutPortalLoginFooter } from "./autogen/AutogenLayoutPortalLoginFooter";
import { PortalCompanySettings } from "./settings/PortalCompanySettings";


const log = LogManager.getLogger("common.Login");

export class PortalLoginFooter extends AutogenLayoutPortalLoginFooter {
    constructor(props?: any) {
        super({
            auth: AuthType.UNAUTH,
            ...props
        });
    }

    public static addFooterToLogin(loginLayout: Login) {
        const labelForgotMyPassword = new Label({
            fillRow: true,
            align: HorizontalAlignment.RIGHT,
            text: "Forgot my password",
            color: "primary",
            allowSelect: true,
        });
        labelForgotMyPassword.addClickListener((event: ClickEvent) => {
            const label = event.target as Label;
            const options = { position: Alignment.RIGHT, shaking: true, timeout: 10000 };
            if (StringUtil.isEmptyString(loginLayout.textUserId.text)) {
                label.showAdhocTooltip("Enter a Login ID to request a password reset link.", options);
            }
            else {
                Api.post("user/reset-password-request", { user_name: loginLayout.textUserId.text }, { appendPrefix: false }).then(response => {
                    label.showAdhocTooltip("If this is a valid Login ID, we sent a password reset link to your email address.", options);
                }).catch(err => {
                    label.showAdhocTooltip("There was an error sending the password reset link.", options);
                    log.error("Password reset error", err)
                });
            }
        });

        loginLayout.panelContentLogin.add(labelForgotMyPassword);
        const footer = Layout.getLayout("portal-common/PortalLoginFooter", null, ) as PortalLoginFooter;
        loginLayout.panelContentLogin.add(footer);
        const settings = PortalCompanySettings.getSingleton();
        footer.addLayoutLoadListener((event: Event) => {
            if (PortalCompanySettings.get().allow_driver_recruiting == false) {
                footer.labelDriverApplication.visible = false;
            }
            const searchItems = footer.textboxSearchType.items as DropdownItem[];
            if (settings.isBrokerageLtl() === true) {
                searchItems.push(new DropdownItem("CarrierProNum", "Carrier Pro #"));
                searchItems.push(new DropdownItem("ShipperPONum", "Shipper PO #"));
            }
            if (settings.isAssetLtl() == true)
                searchItems.push(new DropdownItem("ProNum", "Pro #"));

            footer.buttonTrack.addClickListener((event: ClickEvent) => {
                const searchType = footer.textboxSearchType.selectedItem?.value;
                const trackingNum = footer.textboxTrackingNum.text;
                if (!footer.textboxSearchType.validateSimple())
                    footer.textboxSearchType.showAdhocTooltip("Search Type Required");
                if (!footer.textboxTrackingNum.validateSimple())
                    footer.textboxTrackingNum.showAdhocTooltip("Tracking Number Required");

                if (footer.textboxSearchType.validateSimple() && footer.textboxTrackingNum.validateSimple()) {
                    PortalLoginFooter.createSearchLink(searchType, trackingNum).then(link => {
                        PortalLoginFooter.showOrderByLink(link);
                    }).catch(error => {
                        log.error("Failed to create search link:", error);
                        footer.textboxTrackingNum.showAdhocTooltip("Error creating tracking link. Please try again.");
                    });
                }
            });
        });
    }

    static showOrderByLink(link: string) {
        if (link) {
            try {
                // Extract just the path and query parameters from the full URL
                const url = new URL(link);
                const relativePath = url.pathname + url.search;
                log.debug("Navigating to relative path: {0}", relativePath);
                Navigation.navigateTo(relativePath);
            } catch (error) {
                log.error("Failed to parse URL, trying direct navigation: {0}", error);
                Navigation.navigateTo(link);
            }
        } else {
            Snackbar.showSnackbar("Unable to display data for this tracking number.");
        }
    }

    static async createSearchLink(searchType: string, trackingNum: string) : Promise<string> {
        let ordersLink = "";
        try {
            log.debug("Creating search link for type: {0}, value: {1}", searchType, trackingNum);
            const response = await Api.post("portal/customer/create-unauth-tracking-link", {
                search_type: searchType,
                search_value: trackingNum
            }, { appendPrefix: false });
            ordersLink = response.data[0]?.link;
            log.debug("Received link: {0}", ordersLink);
        } catch (error) {
            log.error("Error creating tracking token:", error);
        }
        return ordersLink;
    }
}
